import React, { useState, useEffect } from 'react';
import { SAPLayout } from '@/layouts/SAPLayout/SAPLayout';
import { mockSapSimulatorService } from '@/services/mock/MockSapSimulatorService';
import { SapVendorMaster } from '@/types/domain/sap-simulator';
import { DataTable, ColumnDef } from '@/components/data-display/DataTable';
import { Button } from '@/components/ui/Button';
import { Database, Plus, Search } from 'lucide-react';
import { SapVendorFormModal } from '@/components/sap/SapVendorFormModal';

export const SapVendorsPage: React.FC = () => {
  const [vendors, setVendors] = useState<SapVendorMaster[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [search, setSearch] = useState('');

  const loadVendors = async () => {
    try {
      setIsLoading(true);
      const data = await mockSapSimulatorService.getVendors();
      setVendors(data.data);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadVendors();
  }, []);

  const handleCreateVendor = async (payload: Omit<SapVendorMaster, 'id' | 'createdAt'>) => {
    await mockSapSimulatorService.createVendor(payload);
    await loadVendors();
  };

  const filteredVendors = vendors.filter(
    (v) =>
      v.vendorCode.toLowerCase().includes(search.toLowerCase()) ||
      v.vendorName.toLowerCase().includes(search.toLowerCase()) ||
      v.category.toLowerCase().includes(search.toLowerCase())
  );

  const columns: ColumnDef<SapVendorMaster>[] = [
    {
      header: 'Vendor Code',
      accessorKey: 'vendorCode',
      cell: (row) => <span className="font-mono font-extrabold text-blue-600 text-xs">{row.vendorCode}</span>,
    },
    {
      header: 'Vendor Name',
      accessorKey: 'vendorName',
      cell: (row) => <span className="font-bold text-xs text-foreground">{row.vendorName}</span>,
    },
    {
      header: 'Category',
      accessorKey: 'category',
      cell: (row) => <span className="text-xs text-foreground-secondary font-medium">{row.category}</span>,
    },
    {
      header: 'GST Number',
      accessorKey: 'gstNumber',
      cell: (row) => <span className="font-mono text-xs text-foreground-muted">{row.gstNumber}</span>,
    },
    {
      header: 'Payment Terms',
      accessorKey: 'paymentTerms',
      cell: (row) => <span className="font-semibold text-xs text-foreground">{row.paymentTerms}</span>,
    },
    {
      header: 'Risk Rating',
      accessorKey: 'riskRating',
      cell: (row) => (
        <span
          className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full ${
            row.riskRating === 'LOW' ? 'bg-emerald-500/10 text-emerald-700' : 'bg-amber-500/10 text-amber-700'
          }`}
        >
          {row.riskRating} Risk
        </span>
      ),
    },
    {
      header: 'Status',
      accessorKey: 'status',
      cell: (row) => (
        <span className="text-[10px] font-extrabold uppercase bg-emerald-500/10 text-emerald-700 px-2 py-0.5 rounded-full">
          {row.status}
        </span>
      ),
    },
  ];

  return (
    <SAPLayout>
      <div className="space-y-6 font-heading">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-extrabold text-foreground flex items-center gap-2">
              <Database className="h-5 w-5 text-blue-600" /> SAP Vendor Master Database
            </h2>
            <p className="text-xs text-foreground-secondary font-medium">
              Registered SAP vendors for ME21N PO generation and MIRO 3-way invoice matching.
            </p>
          </div>

          <Button onClick={() => setIsModalOpen(true)} className="rounded-xl font-bold bg-primary text-white">
            <Plus className="h-4 w-4 mr-1.5" /> Create SAP Vendor Master
          </Button>
        </div>

        {/* Search Bar */}
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-foreground-muted" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search vendor code, vendor name, category..."
            className="w-full rounded-xl border border-border bg-background pl-9 pr-4 py-2 text-xs font-semibold text-foreground focus:outline-hidden focus:ring-2 focus:ring-primary"
          />
        </div>

        <DataTable data={filteredVendors} columns={columns} isLoading={isLoading} />

        <SapVendorFormModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleCreateVendor}
        />
      </div>
    </SAPLayout>
  );
};
